<template>
    <div v-if="uboDeclaration" style="margin: 80px">
        <h1>Declaration of the company’s beneficial owners</h1>
        <h2>1. Information regarding the declaring company</h2>
        <p>
            The company {{ uboDeclaration.company.name }} (company number {{ uboDeclaration.company.registrationNumber }}), whose registered office is
            located at {{ uboDeclaration.company.formattedAddress }}, confirms that the natural person(s) declared in this form is/are the Beneficial
            Owner(s) of the company.
        </p>
        <ul style="margin-top: 25px; list-style: none">
            <li v-for="(owner, i) in uboDeclaration.uniqueBeneficialOwners" :key="i">
                <h3>{{ ownerTitle(i) }}</h3>
                <dl style="display: flex; padding-left: 25px">
                    <dt>Full name:</dt>
                    <dd style="margin-left: 5px">
                        <span style="font-weight: bold">{{ owner.fullName }}</span>
                    </dd>
                </dl>
                <dl style="display: flex; padding-left: 25px">
                    <dt>Full address:</dt>
                    <dd style="margin-left: 5px">
                        <span style="font-weight: bold">{{ owner.formattedAddress }}</span>
                    </dd>
                </dl>
                <dl style="display: flex; padding-left: 25px">
                    <dt>Date of birth:</dt>
                    <dd style="margin-left: 5px">
                        <span style="font-weight: bold">{{ owner.birthday }}</span>
                    </dd>
                </dl>
                <dl style="display: flex; padding-left: 25px">
                    <dt>Place of birth:</dt>
                    <dd style="margin-left: 5px">
                        <span style="font-weight: bold">{{ owner.formattedBirthPlace }}</span>
                    </dd>
                </dl>
                <dl style="display: flex; padding-left: 25px">
                    <dt>Nationality:</dt>
                    <dd style="margin-left: 5px">
                        <span style="font-weight: bold">{{ owner.nationality }}</span>
                    </dd>
                </dl>
                <dl style="display: flex; padding-left: 25px">
                    <dt>Type of control exercised:</dt>
                    <dd v-if="owner.controlType === '1' || owner.controlType === '2'" style="margin-left: 5px">
                        <span style="font-weight: bold">{{ owner.controlType }}</span>
                        with
                        <span style="font-weight: bold">{{ owner.controlDetails }}</span>
                    </dd>
                    <dd v-else-if="owner.controlType === '3'" style="margin-left: 5px">
                        <span style="font-weight: bold">{{ owner.controlType }}</span>
                        please specify
                        <span style="font-weight: bold">{{ owner.controlDetails }}</span>
                    </dd>
                    <dd v-else style="margin-left: 5px">
                        <span style="font-weight: bold">{{ owner.controlType }}</span>
                    </dd>
                </dl>
            </li>
        </ul>
        <h2>2. Additional Statements</h2>
        <div>
            <p>I acknowledge the following:</p>
            <ul>
                <li>There are no other beneficial owners (direct or indirect holding of more than 25%) than those mentioned in the table above</li>
                <li>{{ provider }} will be notified in writing of any modification(s)</li>
                <li>The information transmitted is current and up to date</li>
            </ul>
            <p>
                I acknowledge that the information provided is true and accurate and agree to sign the declaration of beneficial owners
                electronically.
            </p>

            <p>
                Signed in
                <span style="font-weight: bold">{{ uboDeclaration.company.signatureLocation }}</span>
                , on the
                <span style="font-weight: bold">{{ formatDate(signatureDate) }}</span>
            </p>

            <p>
                Full name of the legal representative:
                <span style="font-weight: bold">{{ legalRepresentativeFullName }}</span>
            </p>

            <p>
                Signature of the legal representative:
                <span style="font-family: cursive; font-size: 35px; font-style: italic">{{ legalRepresentativeFullName }}</span>
            </p>

            <p style="margin-top: 75px; font-style: italic">
                Document automatically generated at {{ uboDeclaration.company.signatureLocation }} on {{ formatDateToShortWithHours(signatureDate) }}
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {ref} from 'vue';
    import {useFormat, useRoute} from '#imports';
    import type {UboDeclarationResponse} from '@api';
    import {CompanyUboApi} from '@api';
    import {apiConfiguration} from '~/configuration/apiConfiguration';

    const {formatDate, formatDateToShortWithHours} = useFormat();

    const route = useRoute();
    const companyId = route.params.id as string;
    const provider = route.query.provider as string;
    const companyUboApi = new CompanyUboApi(apiConfiguration);

    const uboDeclaration = ref<UboDeclarationResponse | null>(null);
    uboDeclaration.value = await companyUboApi.companyUbo({companyId});
    const signatureDate = new Date();
    const legalRepresentativeFullName = `${uboDeclaration.value?.legalRepresentative.firstName} ${uboDeclaration.value?.legalRepresentative.lastName}`;

    function ownerTitle(ownerIndex: number) {
        const position = ownerIndex + 1;
        // Determine ordinal suffix (1st, 2nd, 3rd, 4th, 21st, 22nd, 23rd, etc.)
        let suffix = 'th';
        // Exceptions: 11, 12, 13 always use 'th'
        if (position !== 11 && position !== 12 && position !== 13) {
            const mod10 = position % 10;
            if (mod10 === 1) suffix = 'st';
            else if (mod10 === 2) suffix = 'nd';
            else if (mod10 === 3) suffix = 'rd';
        }
        return `${position}${suffix} UBO`;
    }
</script>
